import React from "react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "../../../ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Workflow skills opens on Skills, so Assignment is only
// reachable by name if it says so itself.
declareAiTabDestination({
  id: "workflowMemberSkills.open",
  title: "Open the Workflow skill assignment tab",
  stateName: "app.workflow_skills.assign",
  opens: "page:workflowMemberSkills",
  description:
    "Opens Workflows > Skills > Assignment: which agency members hold which workflow skills, rather than the skills themselves.",
  location: "the tab bar of Workflow skills",
  permissionKey: "manage_workflow_skills",
  examples: ["open skill assignment", "who has the intake skill"],
});

/**
 * What the two Workflow Skills tabs can do for the AI omnibar.
 *
 * Each tab has one search box and they search different things - skills by name, and agency members
 * by name - so they are two capabilities rather than one. Only the tab on screen provides its runner,
 * which is what keeps "search skills for driving" from landing on the members tab.
 */
declareAiCapability({
  id: "workflowSkills.filter",
  title: "Search the workflow skills",
  effect: "prefill",
  description:
    "Sets the search box on Workflows > Skills, which filters the skills by name. That is the only filter this tab has. Only works while that tab is open.",
  location: "the search box of the Workflows > Skills tab",
  scope: "page",
  permissionKey: "manage_workflow_skills",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text matched against the skill's name.",
    },
  ],
  examples: ["workflow skills for nursing"],
});

declareAiCapability({
  id: "workflowMemberSkills.filter",
  title: "Search the workflow skill assignments",
  effect: "prefill",
  description:
    "Sets the search box on Workflows > Skills > Assignment, which filters the agency members by name. That is the only filter this tab has. Only works while that tab is open.",
  location: "the search box of the Workflows > Skills > Assignment tab",
  scope: "page:workflowMemberSkills",
  permissionKey: "manage_workflow_skills",
  params: [
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text matched against the agency member's name.",
    },
  ],
  examples: ["skill assignments for smith"],
});

/** Drives one of the two search boxes, whichever tab mounted this. */
function useSearchCapability(id: string, setSearch: (value: string) => void, what: string): void {
  const setSearchRef = React.useRef(setSearch);
  setSearchRef.current = setSearch;

  useProvideAiCapability(id, (args) => {
    if (args.search === undefined) {
      return `The ${what} tab has only a search box`;
    }

    setSearchRef.current(String(args.search));

    return `Showing ${what}: "${String(args.search)}"`;
  });
}

export function useWorkflowSkillsAiCapabilities(setSearch: (value: string) => void): void {
  useSearchCapability("workflowSkills.filter", setSearch, "skills");
}

export function useWorkflowMemberSkillsAiCapabilities(setSearch: (value: string) => void): void {
  useSearchCapability("workflowMemberSkills.filter", setSearch, "skill assignments");
}
